import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { getStorageUrl } from "@/lib/storage";

export interface GalleryImage {
  id: string;
  image_url: string;
  caption: string | null;
  sequence_order: number;
}

export interface ResolvedGalleryImage extends GalleryImage {
  resolvedUrl: string;
}

/**
 * Active gallery images with storage paths resolved to public URLs.
 *
 * Shared by the gallery strip and the hero photo so both read the same source
 * without a second round trip — react-query dedupes on the key.
 */
export function useGalleryImages() {
  const { data: images, isLoading } = useQuery({
    queryKey: ["gallery-images"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gallery_images")
        .select("*")
        .eq("is_active", true)
        .order("sequence_order", { ascending: true });
      if (error) throw error;
      return data as GalleryImage[];
    },
  });

  const resolvedImages = useMemo<ResolvedGalleryImage[]>(() => {
    if (!images) return [];
    return images.map((img) => ({
      ...img,
      resolvedUrl: getStorageUrl("gallery", img.image_url) || img.image_url,
    }));
  }, [images]);

  return { images: resolvedImages, isLoading };
}
